import React from "react";
import PropTypes from "prop-types";
import { NavLink, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import Icons from "./icons";

import { ChevronDown } from "lucide-react";

const Sidebar = ({ isOpen, toggleSidebar, onNewProject, currentUser }) => {
  const [showHR, setShowHR] = React.useState(false);
  const [showProjects, setShowProjects] = React.useState(false);
  const [selectedHR, setSelectedHR] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
// Remembers the last project URL you visited
  const [storedProjectPath, setStoredProjectPath] = React.useState(
    () => sessionStorage.getItem("lastProjectPath") || "/dashboard/projects"
  );
  const [storedSettingsPath, setStoredSettingsPath] = React.useState(
    () => sessionStorage.getItem("lastSettingsPath") || "/dashboard/settings"
  );

  // Persist to sessionStorage for later use on other pages.
  // Also clear it when the user is back on the plain project list —
  // otherwise the sidebar keeps sending them into the old project.
  React.useEffect(() => {
    if (location.pathname.startsWith("/dashboard/projects/")) {
      sessionStorage.setItem("lastProjectPath", location.pathname);
      setStoredProjectPath(location.pathname);
    } else if (location.pathname === "/dashboard/projects") {
      sessionStorage.removeItem("lastProjectPath");
      setStoredProjectPath("/dashboard/projects");
    }
  }, [location.pathname]);

  React.useEffect(() => {
    if (location.pathname.startsWith("/dashboard/settings")) {
      sessionStorage.setItem("lastSettingsPath", location.pathname);
      setStoredSettingsPath(location.pathname);
    }
  }, [location.pathname]);

  // Computed directly on every render — no flicker when a project page loads
  const lastProjectPath = location.pathname.startsWith("/dashboard/projects/")
    ? location.pathname
    : storedProjectPath;
  const lastSettingsPath = location.pathname.startsWith("/dashboard/settings")
    ? location.pathname
    : storedSettingsPath;

  const isAdminOrManager = ["admin", "manager"].includes(currentUser?.role);

  const isHrActive =
    selectedHR ||
    location.pathname.startsWith("/dashboard/leave") ||
    location.pathname.startsWith("/dashboard/holiday-list");

  const getNavItemClass = ({ isActive }) =>
    `nav-item ${isActive && !selectedHR ? "active" : ""}`;

  const clearSelectedHR = () => {
    setSelectedHR(false);
  };

  // NOTE: intentionally no auto-open of the HR dropdown on HR routes —
  // dropdowns close when an option is selected and only reopen on click.

  const handleHRClick = () => {
    setShowHR((current) => {
      const next = !current;
      setSelectedHR(next);
      return next;
    });
    setShowProjects(false);
  };

  const handleHRChevronClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowHR((current) => {
      const next = !current;
      setSelectedHR(next);
      return next;
    });
    setShowProjects(false);
  };

  return (
        <div className="bg-[#4253C7] rounded-xl">
      <div className={`sidebar ${isOpen ? "open" : "collapsed"}`}>
        {/* HEADER */}
         <div className="sidebar-header">
<div className="logo-container" onClick={toggleSidebar}>
  {isOpen ? (
    <>
<Icons.Logo className="sidebar-logo" />
      <span className="logo-title">QwickHub</span>
    </>
  ) : (
    <Icons.Qwickhub />
  )}
</div>
</div>
        {/* NAV */}
        <ul>
          {/* DASHBOARD */}
        {/* 🔥 PROJECTS (NAV + DROPDOWN) */}
          {/* DASHBOARD */}
  <li className="tooltip-container">
    <NavLink
      to="/dashboard"
      end
      className={getNavItemClass}
      onClick={clearSelectedHR}
    >
      <Icons.Dashboard />
      {isOpen && <span>Dashboard</span>}
    </NavLink>
    {!isOpen && <span className="tooltip font-regular">Dashboard</span>}
  </li>

          {/* 🔥 PROJECTS (NAV + DROPDOWN) */}
          <li className="tooltip-container">
           <NavLink
      to={lastProjectPath}
      className={getNavItemClass}
      onClick={clearSelectedHR}
    >
      <Icons.Projects />

              {isOpen && (
                <>
                  <span>Projects</span>

                  <ChevronDown
                    size={16}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowProjects((current) => !current);
                      setShowHR(false);
                      setSelectedHR(false);
                    }}
                    style={{
                      marginLeft: "auto",
                      cursor: "pointer",
                      transform: showProjects ? "rotate(180deg)" : "rotate(0)",
                    }}
                  />
                </>
              )}
            </NavLink>

            {/* DROPDOWN */}
            {showProjects && isOpen && (
              <ul className="dropdown">
                <li>
                  <NavLink
                    to="/dashboard/my-work"
                    onClick={() => {
                      setShowProjects(false);
                      clearSelectedHR();
                    }}
                    className={({ isActive }) =>
                      `dropdown-item flex items-center gap-2 ${isActive ? "active-dropdown" : ""}`
                    }
                  >
                    <Icons.Work />
                    <span>My Work</span>
                  </NavLink>
                </li>
              </ul>
            )}

            {!isOpen && <span className="tooltip font-regular">Projects</span>}
          </li>

          {/* KANBAN */}
          <li className="tooltip-container">
            <NavLink
              to="/dashboard/kanban"
              className={getNavItemClass}
              onClick={clearSelectedHR}
            >
              <Icons.Kanban />
              {isOpen && <span>Kanban Board</span>}
            </NavLink>
            {!isOpen && (
              <span className="tooltip font-regular">Kanban Board</span>
            )}
          </li>

          {/* REPORTS */}
          {isAdminOrManager && (
            <li className="tooltip-container">
              <NavLink
                to="/dashboard/reports"
                className={getNavItemClass}
                onClick={clearSelectedHR}
              >
                <Icons.Reports />
                {isOpen && <span>Reports</span>}
              </NavLink>
              {!isOpen && <span className="tooltip font-regular">Reports</span>}
            </li>
          )}

          {/* TIMELINE — hidden for viewer accounts */}
          {currentUser?.role !== "viewer" && (
          <li className="tooltip-container">
            <NavLink
              to="/dashboard/timeline"
              className={getNavItemClass}
              onClick={clearSelectedHR}
            >
              <Icons.Timesheet />
              {isOpen && <span>Timesheet</span>}
            </NavLink>
            {!isOpen && <span className="tooltip font-regular">Timesheet</span>}
          </li>
          )}

          {/* QWICK HR — hidden for viewer accounts */}
          {currentUser?.role !== "viewer" && (
          <li className="tooltip-container">
            <div
              className={`nav-item ${isHrActive ? "active" : ""}`}
              onClick={handleHRClick}
            >
              <Icons.Hr />
              {isOpen && (
                <>
                  <span>Qwick HR</span>
                  <div
                    onClick={handleHRChevronClick}
                    style={{ cursor: "pointer", marginLeft: "auto" }}
                  >
                    <ChevronDown
                      size={16}
                      style={{
                        transform: showHR ? "rotate(180deg)" : "rotate(0)",
                      }}
                    />
                  </div>
                </>
              )}
            </div>

            {showHR && isOpen && (
              <ul className="dropdown">
                <li>
                  <NavLink
                    to="/dashboard/leave"
                    onClick={() => {
                      setShowHR(false);
                      setSelectedHR(false);
                    }}
                    className={({ isActive }) =>
                      `dropdown-item flex items-center gap-2 ${isActive ? "active-dropdown" : ""}`
                    }
                  >
                    <Icons.Leave />
                    <span>Leave</span>
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/dashboard/holiday-list"
                    onClick={() => {
                      setShowHR(false);
                      setSelectedHR(false);
                    }}
                    className={({ isActive }) =>
                      `dropdown-item flex items-center gap-2 ${isActive ? "active-dropdown" : ""}`
                    }
                  >
                    <Icons.List />
                    <span>Holiday List</span>
                  </NavLink>
                </li>

                  <li>
                  <NavLink
                    to="/dashboard/my-assets"
                    onClick={() => {
                      setShowHR(false);
                      setSelectedHR(false);
                    }}
                    className={({ isActive }) =>
                      `dropdown-item flex items-center gap-2 ${isActive ? "active-dropdown" : ""}`
                    }
                  >
                    <Icons.Asset />
                    <span>My Assets</span>
                  </NavLink>
                </li>
              </ul>
            )}

            {!isOpen && <span className="tooltip font-regular">Qwick HR</span>}
          </li>
          )}
        </ul>

        {/* BUTTONS */}
        <div className="sidebar-buttons">
          {isOpen && isAdminOrManager && (
            <>
              <button
                onClick={onNewProject}
                className="border bg-white border-cyan-300 w-[163px] h-[40px] rounded-[8px] text-[#112250] font-regular"
              >
                New Project
              </button>
              <button
                onClick={() => navigate(lastSettingsPath)}
                className="border border-white text-white w-[163px] h-[40px] rounded-[8px] mt-2 flex items-center justify-center gap-2"
                style={{
                  border: "1px white solid",
                }}
              >
                <Icons.Settings className="w-4 h-4 bg-white" />
                <span>Settings</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

Sidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggleSidebar: PropTypes.func.isRequired,
  onNewProject: PropTypes.func.isRequired,
};

export default Sidebar;
